// /src/pages/AudioLanguage/AddLanguages.js
import React, { useState, useEffect, useRef } from "react";
import axiosInstance from '../../utils/axiosInstance';
import Select from "react-select";
import {
  Box,
  Typography,
  Button,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Switch,
  CircularProgress,
  useTheme,
  IconButton
} from "@mui/material";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

function AddLanguages({ chatbotId, onClose, setReload, canModify }) {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [audioSrc, setAudioSrc] = useState(null);
  const audioPlayerRef = useRef(null);
  const [isDefaultLanguage, setIsDefaultLanguage] = useState(false);
  const [feedback, setFeedback] = useState({ message: null, type: null });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (audioSrc) {
      if (!audioPlayerRef.current) {
        audioPlayerRef.current = new Audio(audioSrc);
      } else {
        audioPlayerRef.current.src = audioSrc;
      }
    } else {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.removeAttribute('src');
      }
    }
  }, [audioSrc]);

  const fetchDocuments = async (currentChatbotId) => {
    setIsLoading(true);
    setFeedback({ message: null, type: null });
    try {
      const params = currentChatbotId ? { chatbot_id: currentChatbotId } : {};
      const response = await axiosInstance.get(`/get-languages`, { params });
      const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

      if (Array.isArray(responseData)) {
        setDocuments(responseData);
      } else {
        console.error("Received non-array data from /get-languages:", responseData);
        setDocuments([]);
        setFeedback({ message: "Received invalid language data format from server.", type: "error" });
      }
    } catch (error) {
      handleError(error, "fetching languages");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (chatbotId) {
      fetchDocuments(chatbotId);
    }
    return () => {
      audioPlayerRef.current?.pause();
    };
  }, [chatbotId]);

  const handleError = (error, action = "performing action") => {
    let message = `Error ${action}. Please try again.`;
    if (error.response) {
      message = error.response.data.message || `Server Error: ${error.response.status}`;
    } else if (error.request) {
      message = "No response from server. Check network connection or backend status.";
    } else {
      message = error.message;
    }
    setFeedback({ message, type: "error" });
  };

  const languageOptions = React.useMemo(() => {
    if (!Array.isArray(documents)) return [];
    return [...new Set(documents.map((doc) => doc.country))]
      .sort()
      .map((country) => ({
        value: country,
        label: country,
      }));
  }, [documents]);

  const voiceOptions = React.useMemo(() => {
    if (!Array.isArray(documents) || !selectedLanguage || !selectedGender) return [];
    return documents
      .filter(
        (doc) =>
          doc.country === selectedLanguage.value &&
          doc.gender === selectedGender
      )
      .map((doc) => ({
        value: doc.voice_name,
        label: doc.name,
        lang_code: doc.lang_code,
        lang_flag: doc.lang_flag,
        website_lang_code: doc.language,
      }));
  }, [documents, selectedLanguage, selectedGender]);

  const handleLanguageChange = (selectedOption) => {
    setSelectedLanguage(selectedOption);
    setSelectedVoice(null);
    setAudioSrc(null);
    setFeedback({ message: null, type: null });
  };

  const handleGenderChange = (event) => {
    setSelectedGender(event.target.value);
    setSelectedVoice(null);
    setAudioSrc(null);
    setFeedback({ message: null, type: null });
  };

  const handleVoiceChange = (selectedOption) => {
    setSelectedVoice(selectedOption);
    setFeedback({ message: null, type: null });
    if (selectedOption) {
      try {
        const audioFileName = `${selectedOption.value}.wav`;
        const audioPath = require(`../../assets/audio/${audioFileName}`);
        setAudioSrc(audioPath);
      } catch (err) {
        console.error("Error loading audio file:", err);
        setAudioSrc(null);
        setFeedback({ message: `Audio preview for ${selectedOption.label} not found.`, type: "warning" });
      }
    } else {
      setAudioSrc(null);
    }
  };

  const handleDefaultLanguageChange = (event) => {
    setIsDefaultLanguage(event.target.checked);
    setFeedback({ message: null, type: null });
  };

  const handlePlayButtonClick = () => {
    if (audioPlayerRef.current && audioPlayerRef.current.src) {
      audioPlayerRef.current.play().catch(err => {
        console.error("Audio play failed:", err);
        setFeedback({ message: "Could not play audio preview.", type: "error" });
      });
    } else {
      setFeedback({ message: "Select a voice to preview.", type: "warning" });
    }
  };

  const handleApiCall = async () => {
    if (!canModify) {
      handleError({ message: "You don't have permission to add language." }, "adding language");
      return;
    }

    if (!selectedLanguage || !selectedGender || !selectedVoice) {
      setFeedback({ message: "Please select a language, gender, and voice.", type: "error" });
      return;
    }

    const selectedVoiceData = voiceOptions.find(opt => opt.value === selectedVoice.value);
    if (!selectedVoiceData) {
      setFeedback({ message: "Internal error: Could not find selected voice data.", type: "error" });
      return;
    }

    setIsSaving(true);
    setFeedback({ message: null, type: null });

    const data = {
      language: selectedLanguage.value,
      website_lang_code: selectedVoiceData.website_lang_code,
      voice: selectedVoice.value,
      gender: selectedGender,
      default_lang: isDefaultLanguage ? 1 : 0,
      lang_code: selectedVoiceData.lang_code,
      lang_flag: selectedVoiceData.lang_flag,
      chatbot_id: chatbotId
    };

    try {
      const response = await axiosInstance.post(`/add-language-audio`, data);
      setFeedback({ message: response.data.message || "Language added successfully!", type: "success" });

      if (response.status === 201 || response.status === 200) {
        setTimeout(() => {
          onClose();
          setReload();
        }, 1500);
      }
    } catch (error) {
      handleError(error, "adding language");
    } finally {
      setIsSaving(false);
    }
  };

  const customSelectStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      backgroundColor: theme.palette.background.paper,
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: theme.palette.background.paper,
      padding: 0,
      '::-webkit-scrollbar': {
        width: '8px',
      },
      '::-webkit-scrollbar-track': {
        backgroundColor: theme.palette.background.default,
      },
      '::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.divider,
        borderRadius: '4px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.palette.action?.hover || theme.palette.divider,
      },
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
      '&:hover': {
        borderColor: theme.palette.primary.main,
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme.palette.text.primary,
    }),
    input: (provided) => ({
      ...provided,
      color: theme.palette.text.primary,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: theme.palette.text.secondary,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? theme.palette.primary.main
        : state.isFocused
        ? theme.palette.action?.hover || theme.palette.background.default
        : theme.palette.background.paper,
      color: state.isSelected 
        ? theme.palette.primary.contrastText 
        : theme.palette.text.primary,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: theme.palette.action?.selected || theme.palette.primary.light,
      },
    }),
  };

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        p: 3,
        backgroundColor: theme.palette.background.default,
        minHeight: '200px'
      }}>
        <CircularProgress />
        <Typography ml={2}>Loading language options...</Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        p: 3,
        height: '100%',
        overflowY: 'auto',
        backgroundColor: theme.palette.background.default,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.background.default,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.divider,
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: theme.palette.action?.hover || theme.palette.divider,
          },
        },
      }}
    >
      <Typography variant="h5" align="center" mb={4} mt={2} color="textPrimary">
        Add Language Audio
      </Typography>

      {feedback.message && (
        <Alert
          severity={feedback.type === 'error' ? 'error' : feedback.type === 'success' ? 'success' : 'warning'}
          onClose={() => setFeedback({ message: null, type: null })}
          sx={{ mb: 3 }}
        >
          {feedback.message}
        </Alert>
      )}

      <Box mb={5}>
        <FormLabel sx={{ fontWeight: 600, mb: 1.5, display: 'block', fontSize: '0.95rem' }}>
          Select Website Language <span style={{ color: theme.palette.error.main }}>*</span>
        </FormLabel>
        <Select
          options={languageOptions}
          onChange={handleLanguageChange}
          value={selectedLanguage}
          styles={customSelectStyles}
          placeholder="Choose a language..."
          isDisabled={isSaving}
        />
      </Box>

      {selectedLanguage && (
        <Box mb={5}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.95rem' }}>
              Select Gender <span style={{ color: theme.palette.error.main }}>*</span>
            </FormLabel>
            <RadioGroup row value={selectedGender || ''} onChange={handleGenderChange}>
              <FormControlLabel value="male" control={<Radio />} label="Male" disabled={isSaving} />
              <FormControlLabel value="female" control={<Radio />} label="Female" disabled={isSaving} />
            </RadioGroup>
          </FormControl>
        </Box>
      )}

      {selectedLanguage && selectedGender && (
        <Box mb={5}>
          <FormLabel sx={{ fontWeight: 600, mb: 1.5, display: 'block', fontSize: '0.95rem' }}>
            Select Voice <span style={{ color: theme.palette.error.main }}>*</span>
          </FormLabel>
          <Box display="flex" gap={1}>
            <Box flexGrow={1}>
              <Select
                options={voiceOptions}
                onChange={handleVoiceChange}
                value={selectedVoice}
                styles={customSelectStyles}
                placeholder="Choose a voice..."
                isDisabled={!selectedLanguage || !selectedGender || isSaving}
                noOptionsMessage={() => voiceOptions.length === 0 && documents.length > 0 ? 'No voices match selection' : 'Select language and gender first'}
              />
            </Box>
            <IconButton
              color="primary"
              onClick={handlePlayButtonClick}
              disabled={!selectedVoice || isSaving}
              sx={{ border: `1px solid ${theme.palette.divider}` }}
            >
              <PlayArrowIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      <Box mb={5} mt={3}>
        <FormControlLabel
          control={
            <Switch
              checked={isDefaultLanguage}
              onChange={handleDefaultLanguageChange}
              disabled={isSaving}
            />
          }
          label={
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              Set as Default Language for Website
            </Typography>
          }
        />
      </Box>

      {canModify && (
        <Button
          type="button"
          variant="contained"
          fullWidth
          onClick={handleApiCall}
          disabled={!selectedLanguage || !selectedGender || !selectedVoice || isSaving || isLoading}
          size="large"
        >
          {isSaving ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            "Add New Language"
          )}
        </Button>
      )}
    </Box>
  );
}

export default AddLanguages;